import React, { useState, useEffect, useCallback, useRef } from 'react';
import SEOHead from '../components/SEOHead';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFavorites } from '../context/FavoritesContext';
import { HeartIcon as OutlineHeartIcon, ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { HeartIcon as SolidHeartIcon } from '@heroicons/react/24/solid';
import ProductCardSkeleton from '../components/ProductCardSkeleton';
import ProductCard from '../components/ProductCard';
import CategoryShowcase from '../components/CategoryShowcase';
import FeaturedProductsCarousel from '../components/FeaturedProductsCarousel';
import TestimonialsSlider from '../components/TestimonialsSlider';
import InstagramFeed from '../components/InstagramFeed';
import NewArrivals from '../components/NewArrivals';
import api from '../utils/api';
import { getOptimizedImageUrl } from '../utils/imageOptimizer';
import { trackViewItemList } from '../utils/analytics';

// Ana sayfa için CSS animasyonları
const HomePageStyles = () => (
  <style>{`
    @keyframes slideIn {
      0% { opacity: 0; transform: translateY(20px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }
    
    @keyframes glow {
      0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.3); }
      50% { box-shadow: 0 0 40px rgba(139, 92, 246, 0.6); }
    }
  `}</style>
);

// --- NİHAİ BANNER GALERİSİ COMPONENTİ ---
const InteractiveBannerGallery = ({ banners, autoSlideInterval = 3000 }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    // YENİ: Otomatik kaymayı durdurmak için hover durumunu takip eden state
    const [isHovered, setIsHovered] = useState(false);
    const timerRef = useRef(null);

    // Debug log - banner verilerini kontrol et
    // console.log('🎯 Banner Gallery Debug:', {
    //     bannersCount: banners?.length || 0,
    //     banners: banners?.map(banner => ({
    //         title: banner.title,
    //         imageUrl: banner.imageUrl,
    //         hasImage: !!banner.imageUrl
    //     }))
    // });

    // Sonraki banner'a geçiren fonksiyon
    const nextBanner = useCallback(() => {
        if (!banners || banners.length === 0) return;
        setActiveIndex(prev => (prev === banners.length - 1 ? 0 : prev + 1));
    }, [banners]);

    // Önceki banner'a geçiren fonksiyon
    const prevBanner = () => {
        if (!banners || banners.length === 0) return;
        setActiveIndex(prev => (prev === 0 ? banners.length - 1 : prev - 1));
    };

    // GÜNCELLEME: Otomatik kayma ve duraklatma mantığını yöneten useEffect
    useEffect(() => {
        // Eğer fare üzerindeyse veya banner sayısı 1'den az ise zamanlayıcıyı çalıştırma
        if (isHovered || !banners || banners.length <= 1) {
            // Mevcut bir zamanlayıcı varsa temizle
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            return;
        }

        // Zamanlayıcıyı kur
        timerRef.current = setTimeout(nextBanner, autoSlideInterval);

        // Component temizlendiğinde veya yeniden çalıştığında zamanlayıcıyı temizle
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [activeIndex, banners, isHovered, autoSlideInterval, nextBanner]);


    if (!banners || banners.length === 0) {
        return null;
    }

    const activeLink = banners[activeIndex]?.link || '#';

    return (
        // Fare üzerine gelince/ayrılınca isHovered durumunu güncelleyen container
        <div 
            className="w-full max-w-7xl mx-auto mb-12"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Büyük Ana Banner Alanı */}
            <Link to={activeLink} className="block w-full h-56 sm:h-72 md:h-96 rounded-2xl overflow-hidden shadow-lg group relative bg-gray-100">
                {banners.map((banner, index) => (
                    <div key={index} className={`absolute inset-0 w-full h-full transition-opacity duration-700 ease-in-out ${activeIndex === index ? 'opacity-100' : 'opacity-0'}`}>
                        <img
                            src={getOptimizedImageUrl(banner.imageUrl, { width: 1280, quality: 80 })}
                            alt={banner.title || `Banner ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                console.error('❌ Banner resmi yüklenemedi:', {
                                    src: e.target.src,
                                    originalUrl: banner.imageUrl,
                                    bannerTitle: banner.title,
                                    bannerIndex: index
                                });
                                // Hata durumunda placeholder göster
                                e.target.src = 'https://placehold.co/1280x400/e0e0e0/ffffff?text=Banner+Bulunamadi';
                            }}
                            onLoad={(e) => {
                                // console.log('✅ Banner resmi başarıyla yüklendi:', {
                                //     src: e.target.src,
                                //     bannerTitle: banner.title,
                                //     bannerIndex: index
                                // });
                            }}
                        />
                        
                        {/* Flash Sale Overlay */}
                        {banner.type === 'flash_sale' && (
                            <div className="absolute inset-0 bg-gradient-to-r from-red-500/80 to-orange-500/80 flex items-center justify-center">
                                <div className="text-center text-white p-8">
                                    <div className="flex items-center justify-center mb-4">
                                        <div className="bg-white/20 rounded-full px-4 py-2 mr-4">
                                            <span className="font-bold text-lg animate-pulse">
                                                {banner.badge_text || 'FLASH SALE'}
                                            </span>
                                        </div>
                                    </div>
                                    <h2 className="text-3xl md:text-4xl font-bold mb-2">{banner.title}</h2>
                                    <p className="text-lg md:text-xl mb-4">{banner.description}</p>
                                    <div className="text-2xl font-bold">
                                        {banner.discount_type === 'percentage' ? `%${banner.discount_value}` : `${banner.discount_value} TL`} İNDİRİM
                                    </div>
                                    {banner.show_countdown && (
                                        <div className="mt-4 text-sm">
                                            ⏰ Kampanya Bitiyor!
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {/* Cart Campaign Overlay */}
                        {banner.type === 'cart_campaign' && (
                            <div className="absolute inset-0 bg-gradient-to-r from-green-500/80 to-emerald-500/80 flex items-center justify-center">
                                <div className="text-center text-white p-8">
                                    <div className="flex items-center justify-center mb-4">
                                        <div className="bg-white/20 rounded-full px-4 py-2 mr-4">
                                            <span className="font-bold text-lg">
                                                🛒 {banner.badge_text || 'SEPET İNDİRİMİ'}
                                            </span>
                                        </div>
                                    </div>
                                    <h2 className="text-3xl md:text-4xl font-bold mb-2">{banner.title}</h2>
                                    <p className="text-lg md:text-xl mb-4">{banner.description}</p>
                                    <div className="text-2xl font-bold">
                                        {banner.discount_type === 'percentage' ? `%${banner.discount_value}` : `${banner.discount_value} TL`} İNDİRİM
                                    </div>
                                    {banner.min_cart_amount && (
                                        <div className="mt-4 text-sm bg-white/20 rounded-lg px-4 py-2 inline-block">
                                            💰 Minimum {banner.min_cart_amount} TL alışveriş
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {/* Category Campaign Overlay */}
                        {banner.type === 'category_campaign' && (
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/80 to-violet-500/80 flex items-center justify-center">
                                <div className="text-center text-white p-8">
                                    <div className="flex items-center justify-center mb-4">
                                        <div className="bg-white/20 rounded-full px-4 py-2 mr-4">
                                            <span className="font-bold text-lg">
                                                🏷️ {banner.badge_text || 'KATEGORİ KAMPANYASI'}
                                            </span>
                                        </div>
                                    </div>
                                    <h2 className="text-3xl md:text-4xl font-bold mb-2">{banner.title}</h2>
                                    <p className="text-lg md:text-xl mb-4">{banner.description}</p>
                                    <div className="text-xl font-bold">
                                        Seçili kategorilerde özel fiyatlar!
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Coupon Campaign Overlay */}
                        {banner.type === 'coupon_campaign' && (
                            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/80 to-orange-500/80 flex items-center justify-center">
                                <div className="text-center text-white p-8">
                                    <div className="flex items-center justify-center mb-4">
                                        <div className="bg-white/20 rounded-full px-4 py-2 mr-4">
                                            <span className="font-bold text-lg">
                                                🎫 {banner.badge_text || 'KUPON KAMPANYASI'}
                                            </span>
                                        </div>
                                    </div>
                                    <h2 className="text-3xl md:text-4xl font-bold mb-2">{banner.title}</h2>
                                    <p className="text-lg md:text-xl mb-4">{banner.description}</p>
                                    <div className="text-2xl font-bold mb-4">
                                        {banner.discount_type === 'percentage' ? `%${banner.discount_value}` : `${banner.discount_value} TL`} İNDİRİM
                                    </div>
                                    {banner.coupon_code && (
                                        <div className="bg-white/20 rounded-lg px-6 py-3 inline-block">
                                            <div className="text-sm font-medium mb-1">KUPON KODU:</div>
                                            <div className="text-2xl font-black tracking-wider">
                                                {banner.coupon_code}
                                            </div>
                                        </div>
                                    )}
                                    {banner.min_cart_amount && (
                                        <div className="mt-4 text-sm bg-white/20 rounded-lg px-4 py-2 inline-block">
                                            💰 Minimum {banner.min_cart_amount} TL alışveriş
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {/* Package Campaign Overlay */}
                        {banner.type === 'package_campaign' && (
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/80 to-indigo-500/80 flex items-center justify-center">
                                <div className="text-center text-white p-8">
                                    <div className="flex items-center justify-center mb-4">
                                        <div className="bg-white/20 rounded-full px-4 py-2 mr-4">
                                            <span className="font-bold text-lg">
                                                📦 {banner.badge_text || 'PAKET KAMPANYASI'}
                                            </span>
                                        </div>
                                    </div>
                                    <h2 className="text-3xl md:text-4xl font-bold mb-2">{banner.title}</h2>
                                    <p className="text-lg md:text-xl mb-4">{banner.description}</p>
                                    {banner.package_buy_count && banner.package_pay_count && (
                                        <div className="bg-white/20 rounded-lg px-6 py-3 inline-block mb-4">
                                            <div className="text-sm font-medium mb-1">PAKET DETAYI:</div>
                                            <div className="text-2xl font-black">
                                                {banner.package_buy_count} AL {banner.package_pay_count} ÖDE
                                            </div>
                                            <div className="text-lg font-bold text-yellow-200">
                                                {banner.package_buy_count - banner.package_pay_count} ÜRÜN BEDAVA!
                                            </div>
                                        </div>
                                    )}
                                    <div className="text-lg font-bold">
                                        Sepete bir ürün daha ekleyin ve kampanyadan yararlanın!
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Special Day Campaign Overlay */}
                        {banner.type === 'special_day_campaign' && (
                            <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/80 to-orange-500/80 flex items-center justify-center">
                                <div className="text-center text-white p-8">
                                    <div className="flex items-center justify-center mb-4">
                                        <div className="bg-white/20 rounded-full px-4 py-2 mr-4">
                                            <span className="font-bold text-lg">
                                                🎊 {banner.badge_text || 'ÖZEL GÜN'}
                                            </span>
                                        </div>
                                    </div>
                                    <h2 className="text-3xl md:text-4xl font-bold mb-2">{banner.title}</h2>
                                    <p className="text-lg md:text-xl mb-4">{banner.description}</p>
                                    {banner.discount_type && banner.discount_value && (
                                        <div className="bg-white/20 rounded-lg px-6 py-3 inline-block mb-4">
                                            <div className="text-sm font-medium mb-1">İNDİRİM DETAYI:</div>
                                            <div className="text-2xl font-black">
                                                {banner.discount_type === 'percentage' ? `%${banner.discount_value}` : `${banner.discount_value} TL`} İNDİRİM
                                            </div>
                                            <div className="text-lg font-bold text-yellow-200">
                                                Özel gün kampanyasından yararlanın!
                                            </div>
                                        </div>
                                    )}
                                    <div className="text-lg font-bold">
                                        Özel gününüzü kutlayın ve indirimden yararlanın!
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Normal Banner Overlay (eğer title/description varsa) */}
                        {!['flash_sale', 'cart_campaign', 'category_campaign', 'coupon_campaign', 'package_campaign', 'special_day_campaign'].includes(banner.type) && (banner.title || banner.description) && (
                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                <div className="text-center text-white p-8">
                                    {banner.title && <h2 className="text-3xl md:text-4xl font-bold mb-2">{banner.title}</h2>}
                                    {banner.description && <p className="text-lg md:text-xl">{banner.description}</p>}
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                {/* GÜNCELLEME: Ok butonları tekrar eklendi */}
                {banners.length > 1 && (
                    <>
                        <button onClick={prevBanner} className="absolute top-1/2 left-4 transform -translate-y-1/2 bg-white/50 backdrop-blur-sm rounded-full p-2 text-gray-800 hover:bg-white z-20 transition-opacity opacity-0 group-hover:opacity-100" aria-label="Önceki Banner">
                            <ChevronLeftIcon className="h-6 w-6" />
                        </button>
                        <button onClick={nextBanner} className="absolute top-1/2 right-4 transform -translate-y-1/2 bg-white/50 backdrop-blur-sm rounded-full p-2 text-gray-800 hover:bg-white z-20 transition-opacity opacity-0 group-hover:opacity-100" aria-label="Sonraki Banner">
                            <ChevronRightIcon className="h-6 w-6" />
                        </button>
                    </>
                )}
            </Link>

            {/* Alttaki Küçük Resimler (Thumbnail) Alanı */}
            {banners.length > 1 && (
                <div className="flex justify-center items-center space-x-2 sm:space-x-4 mt-4">
                    {banners.map((banner, index) => (
                        <div
                            key={index}
                            onMouseEnter={() => setActiveIndex(index)}
                            className={`w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden cursor-pointer border-2 transition-all duration-300 ${activeIndex === index ? 'border-brand-green scale-110' : 'border-transparent'}`}
                        >
                            <img
                                src={getOptimizedImageUrl(banner.imageUrl, { width: 80, height: 80, quality: 80 })}
                                alt={`Thumbnail ${index + 1}`}
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};


// Ana Sayfa Componenti (Değişiklik yok)
function HomePage() {
    const [products, setProducts] = useState([]);
    const [allProducts, setAllProducts] = useState([]); // Tüm ürünler (filtreleme için)
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [error, setError] = useState(null);
    const { toggleFavorite, isProductFavorited } = useFavorites();
    const navigate = useNavigate();
    const location = useLocation();
    const [settings, setSettings] = useState(null);
    
    // Filtreleme state'leri
    const [selectedCategory, setSelectedCategory] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [priceRange, setPriceRange] = useState({ min: '', max: '' });
    

    useEffect(() => {
        const fetchHomePageData = async () => {
            setLoading(true);
            try {
                const [productsData, settingsData, categoriesData] = await Promise.all([
                    api(`/api/products`),
                    api('/api/settings'),
                    api('/api/categories')
                ]);
                const productList = productsData?.products || productsData || [];
                setAllProducts(productList);
                const initialProducts = productList.slice(0, 8); // İlk 8 ürünü göster
                setProducts(initialProducts);
                
                // GA4: Ana sayfa ürün listesi görüntüleme
                if (initialProducts && initialProducts.length > 0) {
                  trackViewItemList(initialProducts, 'Ana Sayfa - Öne Çıkanlar');
                }
                
                setSettings(settingsData);
                setCategoriesLoading(false);
                
                // Kategorileri düzleştir (hiyerarşik yapıdan düz listeye çevir)
                const flattenCategories = (cats) => {
                    let flattened = [];
                    if (Array.isArray(cats)) {
                        cats.forEach(cat => {
                            flattened.push(cat);
                            if (cat.children && Array.isArray(cat.children)) {
                                flattened = flattened.concat(flattenCategories(cat.children));
                            }
                        });
                    }
                    return flattened;
                };
                
                // console.log('API\'den gelen kategoriler:', categoriesData);
                
                // API response'unu doğru şekilde parse et
                const categoryArray = categoriesData?.data || categoriesData || [];
                // console.log('Parse edilmiş kategoriler:', categoryArray);
                
                const flatCategories = flattenCategories(categoryArray);
                // console.log('Düzleştirilmiş kategoriler:', flatCategories);
                setCategories(flatCategories);
            } catch (err) {
                setError(err.message);
                console.error("Ana sayfa verileri yüklenirken hata:", err);
                toast.error("Veriler yüklenirken bir sorun oluştu.");
            } finally {
                setLoading(false);
            }
        };
        
        fetchHomePageData();
        
        // Misafir sipariş başarı mesajını kontrol et
        if (location.state?.orderSuccess) {
            toast.success(
                <div className="space-y-2">
                    <p className="font-bold text-green-800">🎉 Siparişiniz Alındı!</p>
                    <p className="text-sm text-green-700">
                        Sipariş durumu hakkında <strong>{location.state.guestEmail}</strong> adresine bilgi gönderilecektir.
                    </p>
                    <p className="text-xs text-green-600">
                        Sipariş takibi için email adresinizi kontrol etmeyi unutmayın.
                    </p>
                </div>,
                { 
                    autoClose: 8000,
                    className: 'toast-success-custom'
                }
            );
            
            // State'i temizle
            window.history.replaceState({}, document.title);
        }
    }, []); // Sadece component mount'ta çalışsın - infinite loop'u önle

    // Debug için settings'i konsola yazdır
    useEffect(() => {
        if (settings) {
            // console.log('HomePage - Settings yüklendi:', settings);
            // console.log('HomePage - heroBanners:', settings.heroBanners);
            // console.log('HomePage - hasFlashSale:', settings.hasFlashSale);
        }
    }, [settings]);

    // Filtreleme fonksiyonu
    const applyFilters = useCallback(() => {
        if (!Array.isArray(allProducts)) return;
        let filtered = [...allProducts];

        // Kategori filtresi
        if (selectedCategory) {
            filtered = filtered.filter(product => 
                product.categories && product.categories.some(cat => cat.slug === selectedCategory)
            );
        }

        // Arama filtresi
        if (searchTerm) {
            filtered = filtered.filter(product =>
                product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.description.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Fiyat aralığı filtresi
        if (priceRange.min) {
            filtered = filtered.filter(product => parseFloat(product.price) >= parseFloat(priceRange.min));
        }
        if (priceRange.max) {
            filtered = filtered.filter(product => parseFloat(product.price) <= parseFloat(priceRange.max));
        }

        setProducts(filtered);
    }, [allProducts, selectedCategory, searchTerm, priceRange]);

    // Filtreleri temizle
    const clearFilters = () => {
        setSelectedCategory('');
        setSearchTerm('');
        setPriceRange({ min: '', max: '' });
        setProducts((allProducts || []).slice(0, 8));
    };

    // Filtreleme değiştiğinde ürünleri güncelle
    useEffect(() => {
        if (allProducts.length > 0) {
            applyFilters();
        }
    }, [selectedCategory, searchTerm, priceRange, applyFilters]);

    const handleFavoriteClick = (e, product) => {
        e.preventDefault();
        e.stopPropagation();
        toggleFavorite(product);
    };
    
    const handleProductClick = (e, productId) => {
        e.preventDefault();
        navigate(`/products/${productId}`);
    };

    // Schema.org structured data for homepage
    const homePageSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Minela Kids",
        "url": "https://minelakids.com",
        "logo": "https://minelakids.com/minela_kids_logo.jpg", // Logo URL'sini doğrulayın
        "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+90-555-260-94-20",
            "contactType": "Customer Service",
            "email": "info@minelakids.com"
        },
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "OSMANGAZİ MAH. RASİM SK. 6/1",
            "addressLocality": "SANCAKTEPE",
            "addressRegion": "İstanbul",
            "postalCode": "34887",
            "addressCountry": "TR"
        },
        "sameAs": [
            "https://www.instagram.com/minelakids",
            "https://www.facebook.com/minelakids"
            // Diğer sosyal medya linkleri buraya eklenebilir
        ]
    };

    const firstBannerUrl = settings?.heroBanners?.[0]?.imageUrl;

    // Core Web Vitals optimizasyonu için preconnect domain'leri
    const preconnectDomains = [
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com'
    ];

    // Supabase domain'ini ekleyin (varsa)
    if (firstBannerUrl && firstBannerUrl.includes('supabase')) {
      const supabaseDomain = firstBannerUrl.match(/(https:\/\/[a-zA-Z0-9-]+\.supabase\.co)/);
      if (supabaseDomain && !preconnectDomains.includes(supabaseDomain[1])) {
        preconnectDomains.push(supabaseDomain[1]);
      }
    }

    return (
        <>
            <SEOHead
                title="Minela Kids - En Kaliteli Çocuk Giyim ve Moda | Bebek Kıyafetleri"
                description="Minela Kids'te en kaliteli çocuk giyim ürünlerini keşfedin. Bebek ve çocuk kıyafetleri, ayakkabılar ve aksesuarlar için güvenilir adres. Ücretsiz kargo ve hızlı teslimat."
                keywords="çocuk giyim, minela kids, çocuk kıyafeti, bebek giyim, çocuk mont, çocuk ayakkabı, kız çocuk elbise, erkek çocuk pantolon, çocuk moda, bebek tulumu"
                ogType="website"
                structuredData={homePageSchema}
                preloadImages={firstBannerUrl ? [firstBannerUrl] : []} // LCP imajını önceden yükle
                preconnectDomains={preconnectDomains} // Core Web Vitals optimizasyonu için domain'leri preconnect et
            />
            <HomePageStyles />
            
            {/* Banner - Container içinde */}
            <div className="container mx-auto px-4 py-2">
                {loading ? (
                    <div className="w-full max-w-7xl mx-auto mb-12">
                        <div className="h-56 sm:h-72 md:h-96 rounded-2xl bg-gray-200 animate-pulse"></div>
                        <div className="flex justify-center space-x-4 mt-4">
                            <div className="w-20 h-20 rounded-lg bg-gray-200 animate-pulse"></div>
                            <div className="w-20 h-20 rounded-lg bg-gray-200 animate-pulse"></div>
                            <div className="w-20 h-20 rounded-lg bg-gray-200 animate-pulse"></div>
                        </div>
                    </div>
                ) : (
                    <InteractiveBannerGallery banners={settings?.heroBanners} />
                )}
            </div>

            {/* Kategori Vitrini - FULL WIDTH (Container Dışında) */}
            {!loading && categories.length > 0 && (
                <CategoryShowcase categories={categories} />
            )}

            {/* Featured Products Carousel - FULL WIDTH */}
            {!loading && allProducts.length > 0 && (
                <FeaturedProductsCarousel products={allProducts} />
            )}

            {/* New Arrivals - FULL WIDTH */}
            {!loading && allProducts.length > 0 && (
                <NewArrivals products={allProducts} />
            )}

            {/* Rest of content - Container içinde */}
            <div className="container mx-auto px-4">
                <div id="products-section" className="text-center my-10 pt-8 border-t">
                <h2 className="text-3xl font-bold text-text-dark">Öne Çıkan Ürünler</h2>
                <p className="text-text-light mt-2">En yeni ve en sevilen ürünlerimize göz atın.</p>
            </div>

            {/* Filtreleme Arayüzü */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                <div className="flex flex-col lg:flex-row gap-4 items-center">
                    {/* Arama Kutusu */}
                    <div className="relative flex-1 w-full lg:w-auto">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Ürün ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                        />
                    </div>

                    {/* Kategori Dropdown */}
                    <div className="relative w-full lg:w-64">
                        <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green appearance-none bg-white"
                        >
                            <option value="">Tüm Kategoriler</option>
                            {Array.isArray(categories) && categories.length > 0 ? (
                                categories.map((category) => (
                                    <option key={category.id} value={category.slug}>
                                        {category.name}
                                    </option>
                                ))
                            ) : (
                                <option disabled>Kategoriler yükleniyor...</option>
                            )}
                        </select>
                    </div>

                    {/* Fiyat Aralığı */}
                    <div className="flex gap-2 w-full lg:w-auto">
                        <input
                            type="number"
                            placeholder="Min ₺"
                            value={priceRange.min}
                            onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                            className="w-24 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                        />
                        <span className="flex items-center text-gray-500">-</span>
                        <input
                            type="number"
                            placeholder="Max ₺"
                            value={priceRange.max}
                            onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                            className="w-24 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                        />
                    </div>

                    {/* Temizle Butonu */}
                    {(selectedCategory || searchTerm || priceRange.min || priceRange.max) && (
                        <button
                            onClick={clearFilters}
                            className="flex items-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            <XMarkIcon className="h-4 w-4" />
                            Temizle
                        </button>
                    )}
                </div>

                {/* Hızlı Kategori Butonları */}
                <div className="flex flex-wrap gap-2 mt-4">
                    {Array.isArray(categories) && categories.slice(0, 6).map((category) => (
                        <button
                            key={category.id}
                            onClick={() => setSelectedCategory(category.slug)}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                selectedCategory === category.slug
                                    ? 'bg-brand-green text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-brand-green/10 hover:text-brand-green'
                            }`}
                        >
                            {category.name}
                        </button>
                    ))}
                </div>

                {/* Sonuç Sayısı */}
                <div className="mt-4 text-sm text-gray-600">
                    {products.length} ürün bulundu
                    {(selectedCategory || searchTerm || priceRange.min || priceRange.max) && (
                        <span className="ml-2 text-brand-green font-medium">
                            (filtrelenmiş)
                        </span>
                    )}
                </div>
            </div>


            {error && <p className="text-center text-red-500">{error}</p>}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                {loading ? (
                    Array.from({ length: 8 }).map((_, index) => <ProductCardSkeleton key={index} />)
                ) : (
                    products.map(product => (
                        <ProductCard key={product.id} product={product} />
                    ))
                )}
            </div>
            </div>

            {/* Instagram Feed - FULL WIDTH */}
            <InstagramFeed />

            {/* Testimonials Slider - FULL WIDTH */}
            {!loading && (
                <TestimonialsSlider />
            )}
        </>
    );
}

export default HomePage;
